
import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Filter, Plus, User, Building2, MapPin, Phone, Mail,
  FileText, CreditCard, Calendar, CheckCircle, XCircle, AlertCircle, X,
  Briefcase, Loader2, ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import { Button, Input, Badge, Card, Modal, ConfirmationDialog, FieldHint, InfoTooltip, useToast } from './ui';
import { ClientType } from '../types';
import {
  clientsService, Client as ApiClient, ClientCreate,
  rentalService, loaService, salesService,
} from '../src/services';
import { usePaginatedApi, useMutation } from '../src/hooks/useApi';

// Type adapter: map API client to component-friendly format
interface Client {
  id: string;
  code: string;
  name: string;
  company?: string;
  type: ClientType;
  provenance: string;
  provenance_id: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  categorieClient: 'DETAILLANT' | 'GROSSISTE';
  isVatExempt: boolean;
  isActive: boolean;
  solvencyScore: number;
  createdAt?: string;
}

// Map API client type to frontend enum
const mapClientType = (apiType: ApiClient['type_client']): ClientType => {
  const mapping: Record<ApiClient['type_client'], ClientType> = {
    'PARTICULIER': ClientType.INDIVIDUAL,
    'ENTREPRISE': ClientType.COMPANY,
    'COOPERATIVE': ClientType.COOPERATIVE,
    'ETAT_ONG': ClientType.GOVERNMENT,
  };
  return mapping[apiType] || ClientType.INDIVIDUAL;
};

// Map frontend enum to API type
const mapToApiClientType = (type: ClientType): ApiClient['type_client'] => {
  const mapping: Record<ClientType, ApiClient['type_client']> = {
    [ClientType.INDIVIDUAL]: 'PARTICULIER',
    [ClientType.COMPANY]: 'ENTREPRISE',
    [ClientType.COOPERATIVE]: 'COOPERATIVE',
    [ClientType.GOVERNMENT]: 'ETAT_ONG',
  };
  return mapping[type] || 'PARTICULIER';
};

// Transform API client to component format
const transformClient = (apiClient: ApiClient): Client => ({
  id: apiClient.id,
  code: apiClient.code,
  name: apiClient.nom,
  company: apiClient.raison_sociale,
  type: mapClientType(apiClient.type_client),
  provenance: apiClient.provenance_libelle || 'Non défini',
  provenance_id: apiClient.provenance_id || '',
  email: apiClient.email || '',
  phone: apiClient.telephone || '',
  address: apiClient.adresse || '',
  city: apiClient.ville || '',
  categorieClient: apiClient.categorie_client || 'DETAILLANT',
  isVatExempt: apiClient.is_exonere,
  isActive: apiClient.is_active,
  solvencyScore: apiClient.score_solvabilite || 75,
  createdAt: apiClient.created_at,
});

// --- Formatters ---
const formatCurrency = (val: number) => new Intl.NumberFormat('fr-BJ', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(val);

// --- Sub-components ---

const ClientTypeBadge = ({ type }: { type: ClientType }) => {
  const styles = {
    [ClientType.INDIVIDUAL]: { color: 'blue', label: 'Particulier' },
    [ClientType.COMPANY]: { color: 'purple', label: 'Entreprise' },
    [ClientType.COOPERATIVE]: { color: 'green', label: 'Coopérative' },
    [ClientType.GOVERNMENT]: { color: 'yellow', label: 'État/ONG' },
  };
  const config = styles[type] || styles[ClientType.INDIVIDUAL];
  return <Badge color={config.color as any}>{config.label}</Badge>;
};

const CategorieBadge = ({ categorie }: { categorie: 'DETAILLANT' | 'GROSSISTE' }) => {
  const isGrossiste = categorie === 'GROSSISTE';
  return (
    <Badge color={isGrossiste ? 'purple' : 'gray'}>
      {isGrossiste ? 'Grossiste' : 'Détaillant'}
    </Badge>
  );
};

const SolvencyBadge = ({ score }: { score: number }) => {
  let color: 'green' | 'yellow' | 'red' = 'green';
  if (score < 50) color = 'red';
  else if (score < 80) color = 'yellow';
  
  return (
    <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold border ${color === 'green' ? 'bg-green-100 text-green-800 border-green-200' : color === 'yellow' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : 'bg-red-100 text-red-800 border-red-200'}`}>
      <CreditCard className="w-3 h-3" />
      Score: {score}/100
    </div>
  );
};

// --- Create Client Modal ---

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateClientModal = ({ isOpen, onClose, onSuccess }: CreateClientModalProps) => {
  const [formData, setFormData] = useState<Partial<Client>>({
    type: ClientType.COMPANY,
    categorieClient: 'DETAILLANT',
    provenance: '',
    isActive: true,
    isVatExempt: false
  });
  const [provenances, setProvenances] = useState<{ id: string; nom: string; code: string }[]>([]);

  useEffect(() => {
    if (isOpen) {
      clientsService.listProvenances()
        .then(setProvenances)
        .catch(() => setProvenances([]));
    }
  }, [isOpen]);

  const { mutate, isLoading, error, reset } = useMutation(clientsService.create, {
    onSuccess: () => {
      onSuccess();
      onClose();
      setFormData({
        type: ClientType.COMPANY,
        categorieClient: 'DETAILLANT',
        provenance: '',
        isActive: true,
        isVatExempt: false
      });
    },
  });

  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Frontend validation
    if (!formData.name?.trim()) {
      setValidationError('Le nom du contact principal est obligatoire.');
      return;
    }
    if (!formData.provenance) {
      setValidationError('La provenance est obligatoire. Veuillez sélectionner une provenance.');
      return;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setValidationError('L\'adresse email n\'est pas valide.');
      return;
    }

    const createData: any = {
      nom: formData.name || '',
      raison_sociale: formData.company,
      type_client: mapToApiClientType(formData.type || ClientType.COMPANY),
      categorie_client: formData.categorieClient || 'DETAILLANT',
      email: formData.email || undefined,
      telephone: formData.phone,
      adresse: formData.address,
      ville: formData.city,
      titre_exoneration: formData.isVatExempt ? 'Exonéré' : undefined,
      provenance_id: formData.provenance || undefined,
    };

    await mutate(createData);
  };

  const handleClose = () => {
    reset();
    setValidationError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Nouveau Client">
      <form onSubmit={handleSubmit} className="space-y-4">
        {(error || validationError) && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
            {validationError || error}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Type de Client <span className="text-red-500 ml-0.5">*</span></label>
            <select
              className="w-full border-slate-300 rounded-md shadow-sm text-sm"
              value={formData.type}
              onChange={e => setFormData({...formData, type: e.target.value as ClientType})}
              disabled={isLoading}
            >
              <option value={ClientType.COMPANY}>Entreprise</option>
              <option value={ClientType.INDIVIDUAL}>Particulier</option>
              <option value={ClientType.COOPERATIVE}>Coopérative</option>
              <option value={ClientType.GOVERNMENT}>État / ONG</option>
            </select>
            <FieldHint>Détermine les conditions et documents requis</FieldHint>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie Tarifaire <span className="text-red-500 ml-0.5">*</span></label>
            <select
              className="w-full border-slate-300 rounded-md shadow-sm text-sm"
              value={formData.categorieClient || 'DETAILLANT'}
              onChange={e => setFormData({...formData, categorieClient: e.target.value as 'DETAILLANT' | 'GROSSISTE'})}
              disabled={isLoading}
            >
              <option value="DETAILLANT">Détaillant</option>
              <option value="GROSSISTE">Grossiste</option>
            </select>
            <FieldHint>Détermine le prix appliqué (détail ou gros)</FieldHint>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Provenance <span className="text-red-500">*</span></label>
            <select
              className="w-full border-slate-300 rounded-md shadow-sm text-sm"
              value={formData.provenance}
              onChange={e => setFormData({...formData, provenance: e.target.value})}
              required
              disabled={isLoading}
            >
              <option value="">Sélectionner...</option>
              {provenances.map(p => (
                <option key={p.id} value={p.id}>{p.nom}</option>
              ))}
            </select>
            <FieldHint>Canal d'acquisition pour les statistiques commerciales</FieldHint>
          </div>
        </div>

        {formData.type !== ClientType.INDIVIDUAL && (
           <Input
             label="Raison Sociale"
             placeholder="ex: Agrobusiness SARL"
             tooltip="Nom juridique officiel de l'entreprise ou de l'organisation"
             value={formData.company || ''}
             onChange={e => setFormData({...formData, company: e.target.value})}
           />
        )}
        
        <Input
          label="Nom du Contact Principal"
          placeholder="ex: Jean Dupont"
          required
          hint="Prénom et Nom du responsable ou interlocuteur principal"
          value={formData.name || ''}
          onChange={e => setFormData({...formData, name: e.target.value})}
        />

        <div className="grid grid-cols-2 gap-4">
           <Input
             label="Téléphone"
             placeholder="+229..."
             required
             hint="Format international recommandé : +229 XX XX XX XX"
             value={formData.phone || ''}
             onChange={e => setFormData({...formData, phone: e.target.value})}
           />
           <Input
             label="Email"
             type="email"
             placeholder="contact@email.com"
             hint="Pour l'envoi automatique des devis et factures"
             value={formData.email || ''}
             onChange={e => setFormData({...formData, email: e.target.value})}
           />
        </div>

        <div className="space-y-2">
           <Input 
             label="Adresse" 
             placeholder="Quartier, Rue..." 
             value={formData.address || ''} 
             onChange={e => setFormData({...formData, address: e.target.value})} 
           />
           <Input 
             label="Ville" 
             placeholder="Cotonou" 
             value={formData.city || ''} 
             onChange={e => setFormData({...formData, city: e.target.value})} 
           />
        </div>

        <div className="flex items-center gap-4 pt-2">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              checked={formData.isVatExempt}
              onChange={e => setFormData({...formData, isVatExempt: e.target.checked})}
            />
            <span className="ml-2 text-sm text-slate-700">Exonéré de TVA</span>
            <InfoTooltip text="Cochez si le client dispose d'un titre d'exonération fiscale officiel" />
          </label>
        </div>
        
        {formData.isVatExempt && (
           <div className="bg-blue-50 text-blue-800 text-xs p-2 rounded">
             Un justificatif d'exonération sera requis lors de la facturation.
           </div>
        )}

        <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 mt-4">
          <Button variant="ghost" type="button" onClick={handleClose} disabled={isLoading}>Annuler</Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Création...
              </>
            ) : (
              'Créer le Client'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// --- Client Detail Drawer ---

interface ClientDetailProps {
  client: Client;
  onClose: () => void;
  canWrite?: boolean;
  onRefresh?: () => void;
}

const ClientDetail = ({ client, onClose, canWrite, onRefresh }: ClientDetailProps) => {
  const [activeTab, setActiveTab] = useState<'info' | 'docs' | 'contracts' | 'payments'>('info');
  const [clientContracts, setClientContracts] = useState<any[]>([]);
  const [clientPayments, setClientPayments] = useState<any[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { toast } = useToast();
  const [editProvenances, setEditProvenances] = useState<{ id: string; nom: string }[]>([]);
  const [editForm, setEditForm] = useState({
    name: client.name,
    phone: client.phone,
    email: client.email,
    address: client.address,
    city: client.city,
    provenance_id: client.provenance_id || '',
    categorieClient: client.categorieClient || 'DETAILLANT' as 'DETAILLANT' | 'GROSSISTE',
  });

  useEffect(() => {
    if (isEditing) {
      clientsService.listProvenances()
        .then(setEditProvenances)
        .catch(() => setEditProvenances([]));
    }
  }, [isEditing]);

  useEffect(() => {
    if (activeTab === 'contracts') {
      setContractsLoading(true);
      Promise.all([
        rentalService.listContracts({ client_id: client.id, page_size: 50 }).catch(() => ({ data: [] })),
        loaService.listContracts({ client_id: client.id, page_size: 50 }).catch(() => ({ data: [] })),
      ]).then(([rentals, loas]) => {
        const all = [
          ...(rentals.data || []).map((c: any) => ({
            id: c.id, code: c.code, type: 'Location', status: c.status,
            start_date: c.date_debut, end_date: c.date_fin_prevue,
            total_amount: c.montant_total_prevu || 0,
          })),
          ...(loas.data || []).map((c: any) => ({
            id: c.id, code: c.code, type: 'LOA', status: c.status,
            start_date: c.date_debut, end_date: c.date_fin_prevue,
            total_amount: c.montant_total_finance || 0,
          })),
        ];
        setClientContracts(all);
      }).finally(() => setContractsLoading(false));
    }
  }, [activeTab, client.id]);

  useEffect(() => {
    if (activeTab === 'payments') {
      setPaymentsLoading(true);
      salesService.listPayments({ page_size: 50, client_id: client.id } as any)
        .then(result => setClientPayments(result.data || []))
        .catch(() => setClientPayments([]))
        .finally(() => setPaymentsLoading(false));
    }
  }, [activeTab, client.id]);

  const handleDeactivate = async () => {
    setIsDeactivating(true);
    try {
      await clientsService.toggleActive(client.id, !client.isActive);
      toast(client.isActive ? 'Client désactivé' : 'Client réactivé', 'success');
      onRefresh?.();
      onClose();
    } catch {
      toast('Erreur lors de la modification', 'error');
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (editForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) {
      toast('L\'adresse email n\'est pas valide.', 'error');
      return;
    }
    try {
      await clientsService.update(client.id, {
        nom: editForm.name,
        telephone: editForm.phone,
        email: editForm.email,
        adresse: editForm.address,
        ville: editForm.city,
        categorie_client: editForm.categorieClient,
        ...(editForm.provenance_id ? { provenance_id: editForm.provenance_id } : {}),
      });
      setIsEditing(false);
      toast('Informations mises à jour', 'success');
      onRefresh?.();
      onClose();
    } catch {
      toast('Erreur lors de la mise à jour', 'error');
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full md:w-[700px] bg-white shadow-2xl z-40 transform transition-transform duration-300 overflow-y-auto border-l border-slate-200">
      {/* Header */}
      <div className="bg-slate-50 px-6 py-6 border-b border-slate-200">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-start gap-4">
             <div className="h-16 w-16 bg-white rounded-full flex items-center justify-center text-2xl font-bold text-slate-300 border border-slate-200 shadow-sm">
                {client.company ? <Building2 className="w-8 h-8" /> : <User className="w-8 h-8" />}
             </div>
             <div>
                <h2 className="text-2xl font-bold text-slate-900">{client.company || client.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                   <p className="text-sm text-slate-500 font-mono">{client.code}</p>
                   <span className="text-slate-300">•</span>
                   <ClientTypeBadge type={client.type} />
                   <CategorieBadge categorie={client.categorieClient} />
                   {!client.isActive && <Badge color="red">Inactif</Badge>}
                </div>
             </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="flex gap-4">
           <SolvencyBadge score={client.solvencyScore} />
           {client.isVatExempt && <Badge color="blue">Exonéré TVA</Badge>}
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 border-b border-slate-200 sticky top-0 bg-white z-10">
        <nav className="-mb-px flex space-x-6">
          {['info', 'docs', 'contracts', 'payments'].map((tab) => (
             <button
               key={tab}
               onClick={() => setActiveTab(tab as any)}
               className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                 activeTab === tab 
                   ? 'border-primary-500 text-primary-600'
                   : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
               }`}
             >
               {tab === 'info' && 'Infos Générales'}
               {tab === 'docs' && 'Documents'}
               {tab === 'contracts' && 'Locations & LOA'}
               {tab === 'payments' && 'Historique Paiements'}
             </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'info' && (
          <div className="space-y-6 animate-in fade-in">
             <div className="grid grid-cols-2 gap-6">
               <Card title="Coordonnées" className="col-span-2">
                  <div className="space-y-3 text-sm">
                     <div className="flex gap-3">
                        <User className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                           <p className="font-medium text-slate-700">Contact Principal</p>
                           <p className="text-slate-600">{client.name}</p>
                        </div>
                     </div>
                     <div className="flex gap-3">
                        <Phone className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                           <p className="font-medium text-slate-700">Téléphone</p>
                           <p className="text-slate-600">{client.phone}</p>
                        </div>
                     </div>
                     <div className="flex gap-3">
                        <Mail className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                           <p className="font-medium text-slate-700">Email</p>
                           <p className="text-slate-600">{client.email}</p>
                        </div>
                     </div>
                     <div className="flex gap-3">
                        <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                           <p className="font-medium text-slate-700">Adresse</p>
                           <p className="text-slate-600">{client.address}</p>
                           <p className="text-slate-600">{client.city}</p>
                        </div>
                     </div>
                  </div>
               </Card>

               <Card title="Infos Commerciales">
                 <div className="space-y-3 text-sm">
                   <div>
                     <p className="text-xs text-slate-500 uppercase">Provenance</p>
                     <p className="font-medium text-slate-800">{client.provenance}</p>
                   </div>
                   <div>
                     <p className="text-xs text-slate-500 uppercase">Date Création</p>
                     <p className="font-medium text-slate-800">{client.createdAt ? new Date(client.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</p>
                   </div>
                 </div>
               </Card>

               <Card title="Métriques">
                 <div className="space-y-3 text-sm">
                   <div>
                     <p className="text-xs text-slate-500 uppercase">Total Facturé</p>
                     <p className="font-medium text-slate-800">—</p>
                   </div>
                   <div>
                     <p className="text-xs text-slate-500 uppercase">Encours Actuel</p>
                     <p className="font-medium text-slate-600">—</p>
                   </div>
                 </div>
               </Card>
             </div>
             {isEditing ? (
               <div className="space-y-4 border border-slate-200 rounded-lg p-4">
                 <h4 className="font-bold text-slate-700">Modifier les informations</h4>
                 <div className="grid grid-cols-2 gap-3">
                   <Input label="Nom" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} />
                   <Input label="Téléphone" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} />
                   <Input label="Email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} />
                   <Input label="Ville" value={editForm.city} onChange={e => setEditForm({...editForm, city: e.target.value})} />
                 </div>
                 <Input label="Adresse" value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} />
                 <div>
                   <label className="block text-sm font-medium text-slate-700 mb-1">Provenance</label>
                   <select
                     className="w-full border-slate-300 rounded-md shadow-sm text-sm"
                     value={editForm.provenance_id}
                     onChange={e => setEditForm({...editForm, provenance_id: e.target.value})}
                   >
                     <option value="">Sélectionner...</option>
                     {editProvenances.map(p => (
                       <option key={p.id} value={p.id}>{p.nom}</option>
                     ))}
                   </select>
                 </div>
                 <div>
                   <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie Tarifaire</label>
                   <select
                     className="w-full border-slate-300 rounded-md shadow-sm text-sm"
                     value={editForm.categorieClient}
                     onChange={e => setEditForm({...editForm, categorieClient: e.target.value as 'DETAILLANT' | 'GROSSISTE'})}
                   >
                     <option value="DETAILLANT">Détaillant</option>
                     <option value="GROSSISTE">Grossiste</option>
                   </select>
                 </div>
                 <div className="flex justify-end gap-3">
                   <Button variant="ghost" onClick={() => setIsEditing(false)}>Annuler</Button>
                   <Button onClick={handleSaveEdit}>Enregistrer</Button>
                 </div>
               </div>
             ) : (
               <div className="flex justify-end gap-3">
                 {canWrite && (
                   <Button variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => setShowDeleteConfirm(true)}>Supprimer</Button>
                 )}
                 {canWrite && (
                   <Button variant="ghost" className="text-orange-600 hover:text-orange-700" onClick={() => setShowDeactivateConfirm(true)} disabled={isDeactivating}>
                     {client.isActive ? 'Désactiver Client' : 'Réactiver Client'}
                   </Button>
                 )}
                 {canWrite && (
                   <Button variant="secondary" onClick={() => setIsEditing(true)}>Modifier Infos</Button>
                 )}
               </div>
             )}
          </div>
        )}

        {activeTab === 'contracts' && (
          <div className="space-y-4 animate-in fade-in">
             {contractsLoading ? (
               <div className="text-center py-8">
                 <Loader2 className="w-8 h-8 mx-auto text-slate-400 animate-spin" />
                 <p className="text-slate-500 mt-2">Chargement des contrats...</p>
               </div>
             ) : clientContracts.length > 0 ? (
               <div className="space-y-3">
                 {clientContracts.map((c: any) => (
                   <div key={c.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors flex justify-between items-center">
                      <div>
                        <div className="flex items-center gap-2">
                           <span className="font-bold text-slate-800">{c.type || c.contract_type}</span>
                           <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{c.code || c.id}</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">Du {c.start_date || c.startDate} au {c.end_date || c.endDate}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-900">{formatCurrency(c.total_amount || c.totalAmount || 0)}</p>
                        <Badge color="green">{c.status || 'Actif'}</Badge>
                      </div>
                   </div>
                 ))}
               </div>
             ) : (
               <div className="text-center py-8 text-slate-500">
                 <FileText className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                 <p>Aucun contrat actif ou passé.</p>
               </div>
             )}
          </div>
        )}

        {activeTab === 'docs' && (
           <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-2" />
              <p>Module Documents en cours de développement</p>
           </div>
        )}

        {activeTab === 'payments' && (
           <div className="space-y-4 animate-in fade-in">
             {paymentsLoading ? (
               <div className="text-center py-8">
                 <Loader2 className="w-8 h-8 mx-auto text-slate-400 animate-spin" />
                 <p className="text-slate-500 mt-2">Chargement des paiements...</p>
               </div>
             ) : clientPayments.length > 0 ? (
               <div className="space-y-3">
                 {clientPayments.map((p: any) => (
                   <div key={p.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors flex justify-between items-center">
                     <div>
                       <div className="flex items-center gap-2">
                         <span className="font-bold text-slate-800">{p.code || p.id}</span>
                         <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{p.mode_paiement}</span>
                       </div>
                       <p className="text-sm text-slate-500 mt-1">{new Date(p.date_paiement).toLocaleDateString('fr-FR')}</p>
                     </div>
                     <div className="text-right">
                       <p className="font-bold text-slate-900">{formatCurrency(p.montant)}</p>
                     </div>
                   </div>
                 ))}
               </div>
             ) : (
               <div className="text-center py-8 text-slate-500">
                 <CreditCard className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                 <p>Aucun paiement enregistré</p>
               </div>
             )}
           </div>
        )}
      </div>

      <ConfirmationDialog
        isOpen={showDeactivateConfirm}
        onClose={() => setShowDeactivateConfirm(false)}
        onConfirm={handleDeactivate}
        title={client.isActive ? 'Désactiver le client' : 'Réactiver le client'}
        message={client.isActive
          ? `Êtes-vous sûr de vouloir désactiver le client "${client.company || client.name}" ?`
          : `Êtes-vous sûr de vouloir réactiver le client "${client.company || client.name}" ?`}
        confirmLabel={client.isActive ? 'Désactiver' : 'Réactiver'}
        variant="warning"
      />

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          try {
            await clientsService.delete(client.id);
            toast('Client supprimé', 'success');
            onRefresh?.();
            onClose();
          } catch (err: any) {
            const msg = err?.message || 'Impossible de supprimer ce client';
            toast(msg, 'error');
          }
        }}
        title="Supprimer le client"
        message={`Êtes-vous sûr de vouloir supprimer le client "${client.company || client.name}" ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        variant="danger"
      />
    </div>
  );
};

// --- Main Client Module ---

export const ClientsModule = ({ canWrite }: { canWrite?: boolean }) => {
  const canWriteClients = canWrite ?? false;
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [provenances, setProvenances] = useState<{ id: string; nom: string; code: string }[]>([]);

  // Load provenances on mount
  useEffect(() => {
    clientsService.listProvenances()
      .then(setProvenances)
      .catch(() => setProvenances([]));
  }, []);

  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [provFilter, setProvFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('active');

  // Sorting
  type ClientSortKey = 'nom' | 'type' | 'contact' | 'status' | 'score';
  const [clientSort, setClientSort] = useState<{ key: ClientSortKey; dir: 'asc' | 'desc' }>({ key: 'nom', dir: 'asc' });

  const clientSortKeyMap: Record<ClientSortKey, string> = {
    nom: 'nom', type: 'type_client', contact: 'telephone',
    status: 'is_active', score: 'score_solvabilite',
  };

  const toggleClientSort = (key: ClientSortKey) => {
    setClientSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
    setPagination(p => ({ ...p, page: 1 }));
  };

  const ClientSortIcon = ({ col }: { col: ClientSortKey }) => {
    if (clientSort.key !== col) return <ArrowUpDown className="w-3 h-3 ml-1 inline opacity-30" />;
    return clientSort.dir === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 inline text-primary-600" />
      : <ArrowDown className="w-3 h-3 ml-1 inline text-primary-600" />;
  };

  // API state
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalItems: 0 });

  // Fetch clients from API
  const fetchClients = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: any = {
        page: pagination.page,
        page_size: 20,
        sort_by: clientSortKeyMap[clientSort.key],
        sort_dir: clientSort.dir,
      };
      if (search) params.search = search;
      if (typeFilter !== 'all') params.type = mapToApiClientType(typeFilter as ClientType);
      if (statusFilter === 'active') params.active_only = true;
      else params.active_only = false; // "all" or "inactive" — show both active and inactive

      const result = await clientsService.list(params);
      setClients(result.data.map(transformClient));
      setPagination({
        page: result.pagination.page,
        totalPages: result.pagination.total_pages,
        totalItems: result.pagination.total_items,
      });
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des clients');
      setClients([]);
    } finally {
      setIsLoading(false);
    }
  }, [search, typeFilter, statusFilter, provFilter, pagination.page, clientSort]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPagination(p => ({ ...p, page: 1 }));
  }, [search, typeFilter, statusFilter, provFilter]);

  // Fetch on mount and when filters change
  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchClients();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchClients]);

  // Local filtering for provenance (until backend supports it) and status
  const filteredClients = clients.filter(c => {
    const matchesProv = provFilter === 'all' || c.provenance === provFilter;
    const matchesStatus = statusFilter !== 'inactive' || !c.isActive;
    return matchesProv && matchesStatus;
  });

  const handleClientClick = (c: Client) => {
    setSelectedClient(c);
    setIsDetailOpen(true);
  };

  const { toast } = useToast();

  const handleCreateSuccess = () => {
    toast('Client créé avec succès', 'success');
    fetchClients();
  };

  return (
    <div className="relative min-h-[500px]">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
           <h2 className="text-2xl font-bold text-slate-800">Clients & Provenance</h2>
           <p className="text-sm text-slate-500">Gérez votre portefeuille clients et analysez leur origine.</p>
        </div>
        {canWriteClients && <Button icon={Plus} onClick={() => setIsCreateModalOpen(true)}>Nouveau Client</Button>}
      </div>

      {/* Filters Bar */}
      <div className="bg-white p-4 rounded-lg border border-slate-200 mb-6 space-y-4 md:space-y-0 md:flex gap-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input 
            className="pl-9 w-full border-slate-300 rounded-md text-sm py-2 focus:ring-primary-500 focus:border-primary-500" 
            placeholder="Nom, Raison sociale, Code..." 
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        
        <select 
          className="border-slate-300 rounded-md text-sm py-2 bg-slate-50"
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
        >
          <option value="all">Tous types</option>
          <option value={ClientType.COMPANY}>Entreprises</option>
          <option value={ClientType.INDIVIDUAL}>Particuliers</option>
          <option value={ClientType.COOPERATIVE}>Coopératives</option>
          <option value={ClientType.GOVERNMENT}>État / ONG</option>
        </select>

        <select
          className="border-slate-300 rounded-md text-sm py-2 bg-slate-50"
          value={provFilter}
          onChange={e => setProvFilter(e.target.value)}
        >
          <option value="all">Toutes provenances</option>
          {provenances.map(p => (
            <option key={p.id} value={p.nom}>{p.nom}</option>
          ))}
        </select>

        <div className="flex items-center bg-slate-100 rounded-md p-1">
           <button
             onClick={() => setStatusFilter('active')}
             className={`px-3 py-1.5 text-xs font-medium rounded ${statusFilter === 'active' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
           >
             Actifs
           </button>
           <button
             onClick={() => setStatusFilter('inactive')}
             className={`px-3 py-1.5 text-xs font-medium rounded ${statusFilter === 'inactive' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
           >
             Inactifs
           </button>
           <button
             onClick={() => setStatusFilter('all')}
             className={`px-3 py-1.5 text-xs font-medium rounded ${statusFilter === 'all' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
           >
             Tous
           </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleClientSort('nom')}>Client / Société<ClientSortIcon col="nom" /></th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleClientSort('type')}>Type<ClientSortIcon col="type" /></th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Provenance</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleClientSort('contact')}>Contact<ClientSortIcon col="contact" /></th>
              <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleClientSort('status')}>Statut<ClientSortIcon col="status" /></th>
              <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleClientSort('score')}>Score<ClientSortIcon col="score" /></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {!isLoading && !error && filteredClients.map((c) => (
              <tr key={c.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => handleClientClick(c)}>
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 mr-3 border border-slate-200">
                       {c.company ? <Building2 className="w-5 h-5" /> : <User className="w-5 h-5" />}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-slate-900">{c.company || c.name}</div>
                      {c.company && <div className="text-xs text-slate-500">{c.name}</div>}
                      <div className="text-xs text-slate-400 font-mono mt-0.5">{c.code}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                   <div className="flex flex-col gap-1">
                     <ClientTypeBadge type={c.type} />
                     <CategorieBadge categorie={c.categorieClient} />
                   </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                   <div className="flex items-center gap-2">
                     <MapPin className="w-3 h-3 text-slate-400" />
                     {c.provenance}
                   </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-slate-900">{c.phone}</div>
                  <div className="text-xs text-slate-500">{c.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                   {c.isActive ? (
                     <Badge color="green" className="inline-flex gap-1"><CheckCircle className="w-3 h-3" /> Actif</Badge>
                   ) : (
                     <Badge color="gray" className="inline-flex gap-1"><XCircle className="w-3 h-3" /> Inactif</Badge>
                   )}
                   {c.isVatExempt && <div className="mt-1"><Badge color="blue" className="text-[10px] px-1.5">No TVA</Badge></div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex justify-end">
                    <SolvencyBadge score={c.solvencyScore} />
                  </div>
                </td>
              </tr>
            ))}
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                  <Loader2 className="w-8 h-8 mx-auto text-slate-400 animate-spin mb-3" />
                  <p>Chargement des clients...</p>
                </td>
              </tr>
            )}
            {!isLoading && error && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-red-500">
                  <AlertCircle className="w-8 h-8 mx-auto text-red-400 mb-3" />
                  <p>{error}</p>
                  <Button variant="ghost" onClick={fetchClients} className="mt-2">
                    Réessayer
                  </Button>
                </td>
              </tr>
            )}
            {!isLoading && !error && filteredClients.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                  <div className="mx-auto w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                    <Search className="w-6 h-6 text-slate-400" />
                  </div>
                  <p>Aucun client trouvé avec ces critères.</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between bg-white px-4 py-3 border border-slate-200 rounded-lg">
          <div className="text-sm text-slate-500">
            {pagination.totalItems} client{pagination.totalItems > 1 ? 's' : ''} au total
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
            >
              Précédent
            </Button>
            <span className="text-sm text-slate-600">
              Page {pagination.page} sur {pagination.totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
            >
              Suivant
            </Button>
          </div>
        </div>
      )}

      {/* Details Drawer */}
      {isDetailOpen && selectedClient && (
        <>
          <div className="fixed inset-0 bg-slate-900/50 z-30 transition-opacity" onClick={() => setIsDetailOpen(false)} />
          <ClientDetail client={selectedClient} onClose={() => setIsDetailOpen(false)} canWrite={canWriteClients} onRefresh={fetchClients} />
        </>
      )}

      {/* Create Modal */}
      <CreateClientModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};
